<script setup lang="ts">
import { copyToClipboard } from '@/utils/apostatudo/clipboard';

// Etapa 2 dos provedores que pagam por código copiado fora do app: SPEI
// (número CLABE) e OXXO (código de barras). Mesma tela nos dois, só muda o
// campo da resposta e a copy — o fluxo antigo tem um componente pra cada
// (components/deposit/result/{Spei,Oxxo}.vue). São métodos de MXN, fora do
// alcance de quem usa BRL, mas a lista de provedores muda em runtime.

type Props = {
  deposit: DefaultDepositInfoResponse;
  method: string;
};

const { deposit, method } = defineProps<Props>();

type Emits = {
  back: [];
  paid: [];
};

const emit = defineEmits<Emits>();

// O fluxo antigo dá 10 minutos nos dois (WidgetsCountdown :total-time="600").
const TIME_TO_EXPIRE = 600;

const isOxxo = computed(() => method === 'oxxo');

const codeToCopy = computed(() =>
  isOxxo.value ? String(deposit.barcode_number || '') : deposit.clabe || '',
);

const labels = computed(() =>
  isOxxo.value
    ? {
        title: 'deposit.oxxo.result_title',
        copy: 'deposit.oxxo.copy_code_button',
        copied: 'deposit.oxxo.code_copied',
        paid: 'deposit.oxxo.confirm_payment',
        timeToPay: 'deposit.oxxo.time_to_pay',
        timeFinished: 'deposit.oxxo.time_finished',
      }
    : {
        title: 'deposit.spei.copy_code',
        copy: 'deposit.spei.copy_code_button',
        copied: 'deposit.spei.code_copied',
        paid: 'deposit.spei.confirm_payment',
        timeToPay: 'deposit.spei.time_to_pay',
        timeFinished: 'deposit.spei.time_finished',
      },
);

const inputCodeElement = ref<HTMLInputElement | null>(null);
const isCodeCopied = ref(false);
const isExpired = ref(false);

async function copyCode() {
  if (!codeToCopy.value) return;

  try {
    inputCodeElement.value?.select?.();
  } catch {}

  if (await copyToClipboard(codeToCopy.value)) {
    isCodeCopied.value = true;
    setTimeout(() => (isCodeCopied.value = false), 2000);
  }
}

function onTimerEnded() {
  isExpired.value = true;
}
</script>

<template>
  <div class="gdm-code">
    <p class="gdm-code-title">{{ $t(labels.title) }}</p>

    <!-- Código de barras (só OXXO devolve a imagem) -->
    <div
      v-if="isOxxo && deposit.barcode"
      class="gdm-code-barcode"
    >
      <img
        alt="Código de barras"
        :src="deposit.barcode"
      />
    </div>

    <div class="gdm-code-box">
      <p class="gdm-code-value">{{ deposit.amount }}</p>

      <div class="gdm-code-row">
        <input
          ref="inputCodeElement"
          class="gdm-code-input"
          readonly
          type="text"
          :value="codeToCopy"
        />
        <button
          class="gdm-code-copy"
          type="button"
          @click="copyCode"
        >
          <NuxtIcon :name="isCodeCopied ? 'check' : 'copy'" />
          {{ isCodeCopied ? $t(labels.copied) : $t(labels.copy) }}
        </button>
      </div>
    </div>

    <p
      v-if="!isExpired"
      class="gdm-code-status"
    >
      {{ $t(labels.timeToPay) }}
    </p>
    <p
      v-else
      class="gdm-code-status is-expired"
    >
      {{ $t(labels.timeFinished) }}
    </p>

    <!-- O contador roda escondido só pra virar o aviso de expirado, igual ao
         StepPix. -->
    <div class="gdm-code-timer-driver">
      <WidgetsCountdown
        :total-time="TIME_TO_EXPIRE"
        @timer-ended="onTimerEnded"
      >
        <template #process><span /></template>
        <template #finish><span /></template>
      </WidgetsCountdown>
    </div>

    <div class="gdm-code-actions">
      <button
        class="gdm-code-back"
        type="button"
        @click="emit('back')"
      >
        &#8592; {{ $t('deposit.general.go_back') }}
      </button>

      <button
        class="gdm-code-paid"
        type="button"
        @click="emit('paid')"
      >
        {{ $t(labels.paid) }}
      </button>
    </div>
  </div>
</template>

<style scoped lang="scss">
$orange: #ff3700;
$border: #28282c;

.gdm-code {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  font-family: 'Plus Jakarta Sans', sans-serif;
}

.gdm-code-title {
  color: rgba(255, 255, 255, 0.8);
  font-size: 13px;
  line-height: 18px;
  text-align: center;
}

.gdm-code-barcode {
  display: flex;
  justify-content: center;
  padding: 12px;
  border-radius: 12px;
  background: #fff;

  img {
    max-width: 100%;
    height: auto;
  }
}

.gdm-code-box {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  padding: 12px;
  border: 1px solid $border;
  border-radius: 12px;
}

.gdm-code-value {
  color: #fff;
  font-size: 20px;
  font-weight: 700;
  text-align: center;
}

.gdm-code-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.gdm-code-input {
  flex: 1;
  min-width: 0;
  height: 36px;
  padding: 0 10px;
  border: 1px solid $border;
  border-radius: 8px;
  background: transparent;
  color: rgba(255, 255, 255, 0.7);
  font-size: 12px;
}

.gdm-code-copy {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 12px;
  border-radius: 8px;
  background: $orange;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}

.gdm-code-status {
  color: rgba(255, 255, 255, 0.6);
  font-size: 12px;
  text-align: center;

  &.is-expired {
    color: #ef4444;
  }
}

.gdm-code-timer-driver {
  height: 0;
  overflow: hidden;
}

.gdm-code-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
}

.gdm-code-back {
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  height: 40px;
  border: 1px solid $border;
  border-radius: 12px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
}

.gdm-code-paid {
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  height: 40px;
  border-radius: 12px;
  background: $orange;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
</style>
